import { faCheckCircle, faEnvelope } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useState } from 'react'
import { useForm } from 'react-hook-form'

interface MailingListFormValues {
  firstName: string
  lastName: string
  email: string
}

export default function MailingList() {
  const [submitting, setSubmitting] = useState(false)
  const [success, setSuccess] = useState(false)
  const [alreadySubscribed, setAlreadySubscribed] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const {
    register,
    reset,
    handleSubmit: rhfHandleSubmit,
  } = useForm<MailingListFormValues>({
    mode: 'onChange',
  })

  const onSubmit = async (data: MailingListFormValues) => {
    try {
      setError(null)
      setSuccess(false)
      setAlreadySubscribed(false)
      setSubmitting(true)

      const body = {
        email: data.email,
        subscribed: true,
        data: {
          firstName: data.firstName,
          lastName: data.lastName,
        },
      }

      const res = await fetch('/api/mailing-list', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      })

      if (!res.ok) {
        if (res.status === 409) {
          try {
            const payload: unknown = await res.json()

            if (
              typeof payload === 'object' &&
              payload !== null &&
              'message' in payload &&
              (payload as { message?: unknown }).message ===
                'Contact already exists'
            ) {
              setAlreadySubscribed(true)
              reset()
              return
            }
          } catch {
            // fall through to generic error
          }
        }
        throw new Error('Request failed with status ' + String(res.status))
      }

      setSuccess(true)
      reset()
    } catch (err) {
      console.error(err)
      setError('Something went wrong. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <section className="mt-6 w-full">
      <div className="bg-linear-to-r rounded-lg border border-neutral-200 from-neutral-50 to-neutral-100 px-5 py-4 dark:border-neutral-700 dark:from-neutral-800/50 dark:to-neutral-900/50">
        <div className="flex flex-col gap-4 sm:justify-between lg:flex-row lg:items-center">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-neutral-200 text-neutral-600 dark:bg-neutral-700 dark:text-neutral-300">
              <FontAwesomeIcon icon={faEnvelope} className="h-4 w-4" />
            </div>
            <div>
              {/* <h3 className="text-lg font-semibold tracking-tight">
                Join our mailing list
              </h3> */}
              <span className="text-sm font-medium text-neutral-900 dark:text-white">
                <b>Stay in the loop</b> by subscribing to our bi-annual digest
                of albums, EPs, soundtracks & upcoming events related to the
                studio.
              </span>
            </div>
          </div>

          {success ? (
            <div className="text-sm flex items-center gap-2 text-green-700 dark:text-green-400">
              <FontAwesomeIcon icon={faCheckCircle} />
              <span>You're subscribed!</span>
            </div>
          ) : alreadySubscribed ? (
            <div className="text-sm flex items-center gap-2 rounded-md px-2 py-1 text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-950/50 dark:text-emerald-300 dark:ring-emerald-800">
              <FontAwesomeIcon icon={faCheckCircle} />
              <span>You're already subscribed.</span>
            </div>
          ) : (
            <form
              className="flex flex-col gap-2 sm:flex-row sm:items-center"
              onSubmit={(event) => {
                void rhfHandleSubmit(onSubmit)(event)
              }}
            >
              <input
                id="mailing-firstName"
                type="text"
                autoComplete="given-name"
                {...register('firstName', {
                  required: true,
                  minLength: 2,
                })}
                className="text-base h-10 w-full rounded-md border border-neutral-300 bg-white px-3 text-neutral-900 shadow-sm transition placeholder:text-neutral-400 focus:border-neutral-500 focus:outline-none focus:ring-2 focus:ring-neutral-500/20 dark:border-neutral-600 dark:bg-neutral-800 dark:text-white dark:placeholder:text-neutral-500 sm:w-28"
                placeholder="First name"
              />
              <input
                id="mailing-lastName"
                type="text"
                autoComplete="family-name"
                {...register('lastName', {
                  required: true,
                  minLength: 2,
                })}
                className="text-base h-10 w-full rounded-md border border-neutral-300 bg-white px-3 text-neutral-900 shadow-sm transition placeholder:text-neutral-400 focus:border-neutral-500 focus:outline-none focus:ring-2 focus:ring-neutral-500/20 dark:border-neutral-600 dark:bg-neutral-800 dark:text-white dark:placeholder:text-neutral-500 sm:w-28"
                placeholder="Last name"
              />
              <input
                id="mailing-email"
                type="email"
                autoComplete="email"
                inputMode="email"
                {...register('email', {
                  required: true,
                  pattern: /.+@.+\..+/,
                })}
                className="text-base h-10 w-full rounded-md border border-neutral-300 bg-white px-3 text-neutral-900 shadow-sm transition placeholder:text-neutral-400 focus:border-neutral-500 focus:outline-none focus:ring-2 focus:ring-neutral-500/20 dark:border-neutral-600 dark:bg-neutral-800 dark:text-white dark:placeholder:text-neutral-500 sm:w-48"
                placeholder="Email"
              />
              <button
                type="submit"
                disabled={submitting}
                className="text-base h-10 shrink-0 rounded-md bg-neutral-800 px-4 font-medium text-white transition hover:bg-neutral-900 focus:outline-none focus:ring-2 focus:ring-neutral-500/30 disabled:cursor-not-allowed disabled:opacity-70 dark:bg-neutral-100 dark:text-neutral-900 dark:hover:bg-white"
              >
                {submitting ? 'Subscribing…' : 'Subscribe'}
              </button>
            </form>
          )}
          {error && (
            <p className="text-xs mt-2 text-red-600 dark:text-red-400 sm:mt-0 sm:text-left">
              {error}
            </p>
          )}
        </div>
      </div>
    </section>
  )
}
